// src/components/das-application/ApplicationPDF.js
import React, { memo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { sanitizePdfData, sanitizePdfText } from "../utils/pdfSanitize";

const MAX_DAS_LINE_LENGTH = 240;
const MAX_DAS_PARAGRAPH_LENGTH = 1800;
const MAX_DAS_ITEMS_PER_SECTION = 12;

const clipPdfText = (value, maxLength = MAX_DAS_LINE_LENGTH) => {
  const safeValue = sanitizePdfText(value);
  if (safeValue.length <= maxLength) {
    return safeValue;
  }

  return `${safeValue.slice(0, maxLength)}...`;
};

const normalizeDasCategoryCode = (step2 = {}) => {
  const rawValue =
    step2?.scholarship_category ||
    step2?.scholarshipCategory ||
    step2?.category ||
    "";

  const normalizedValue = sanitizePdfText(rawValue).trim().toUpperCase();

  if (normalizedValue) {
    const matchedCode = [
      "APLSK",
      "COMSERV",
      "FINE",
      "INDLC",
      "LNG",
      "PHYSAC",
      "TRADES",
    ].find(
      (code) =>
        normalizedValue === code ||
        normalizedValue.startsWith(`${code}:`) ||
        normalizedValue.includes(code)
    );

    if (matchedCode) {
      return matchedCode;
    }
  }

  return [
    "APLSK",
    "COMSERV",
    "FINE",
    "INDLC",
    "LNG",
    "PHYSAC",
    "TRADES",
  ].find((code) => {
    const legacyValue = step2?.[`das_${code}`];
    return legacyValue === true || legacyValue === "true" || legacyValue === 1;
  });
};

const styles = StyleSheet.create({
  page: {
    position: "relative",
    padding: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
    lineHeight: 1.5,
  },
  headerContainer: {
    position: "absolute",
    top: 20,
    right: 40,
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  logo: {
    width: 100,
    height: 40,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "bold",
    width: "100%",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 10,
    color: "grey",
  },
  firstSection: {
    width: "100%",
    marginTop: 25,
  },
  section: {
    width: "100%",
    marginTop: 10,
    marginBottom: 25,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 5,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  sectionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    margin: 0,
    padding: 0,
  },
  sectionRowNoBreakWords: {
    flexDirection: "row",
    justifyContent: "space-between",
    margin: 0,
    padding: 0,
  },
  sectionRowWithMargin: {
    flexDirection: "row",
    justifyContent: "space-between",
    margin: 0,
    padding: 0,
    marginTop: 7.5,
    marginBottom: 7.5,
  },
  sectionHalfRowItem: {
    width: "50%",
    margin: 0,
    padding: 0,
  },
  sectionRowItem: {
    width: "100%",
    margin: 0,
    padding: 0,
  },
  stackedTextRow: {
    width: "100%",
    margin: 0,
    padding: 0,
    marginBottom: 2,
  },
  stackedValueText: {
    width: "100%",
    margin: 0,
    padding: 0,
    fontSize: 10,
    fontWeight: 500,
    color: "#585858",
    lineHeight: 1.35,
  },
  sectionQuarterRowItem: {
    width: "25%",
    margin: 0,
    padding: 0,
  },
  sectionContentRow: {
    width: "100%",
    flexDirection: "row",
    margin: 0,
    padding: 0,
    alignItems: "flex-start",
  },
  contentLabel: {
    fontSize: 10,
    margin: 0,
    padding: 0,
    fontWeight: 900,
    color: "black",
    width: 100,
  },
  contentValue: {
    fontSize: 10,
    margin: 0,
    padding: 0,
    fontWeight: 500,
    color: "#585858",
    flex: 1,
    lineHeight: 1.75,
  },
  contentLabelLongRow: {
    fontSize: 10,
    margin: 0,
    padding: 0,
    fontWeight: 900,
    color: "black",
    width: "50%",
  },
  contentLabelQuarterRow: {
    fontSize: 10,
    margin: 0,
    padding: 0,
    fontWeight: 900,
    color: "black",
    width: "25%",
  },
  contentValueLongRow: {
    fontSize: 10,
    margin: 0,
    padding: 0,
    fontWeight: 500,
    color: "#585858",
    flex: 1,
    lineHeight: 1.75,
  },
  contentText: {
    marginBottom: 2.5,
    textAlign: "justify",
  },
  additionalMargin: {
    marginVertical: 15,
  },
  listItem: {
    marginLeft: 10,
    marginBottom: 3,
  },
  horizontalLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginVertical: 5,
    width: "100%",
    alignSelf: "center",
  },
  rowMargin: {
    marginVertical: 7,
    width: "100%",
  },
});

const ApplicationPDF = ({ data }) => {
  const safeData = sanitizePdfData(data || {});
  const {
    step1 = {},
    step2 = {},
    step3 = {},
    step4 = [],
    step5 = [],
  } = safeData;
  const schoolName = step1?.schoolName || "N/A";

  function formatYMDToMDY(ymdString) {
    if (!ymdString) return "";
    const [year, month, day] = ymdString.split("-");
    return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
  }

  const generatedDate = sanitizePdfText(new Date().toLocaleString());

  const courses = step4
    ?.filter((item) => item.Type === "Course")
    .slice(0, MAX_DAS_ITEMS_PER_SECTION);
  const extracurriculars = step4
    ?.filter((item) => item.Type === "Extracurricular")
    .slice(0, MAX_DAS_ITEMS_PER_SECTION);
  const awards = step4
    ?.filter((item) => item.Type === "Award")
    .slice(0, MAX_DAS_ITEMS_PER_SECTION);
  const references = step5?.slice(0, MAX_DAS_ITEMS_PER_SECTION) || [];
  const programsApplyingFor = (
    step2?.programs_applying_for ||
    step2?.programsApplyingFor ||
    ""
  )
    .split("\n")
    .map((program) => clipPdfText(program.trim()))
    .filter(Boolean);
  const programsApplyingForText =
    programsApplyingFor.length > 0
      ? programsApplyingFor.map((program) => `• ${program}`).join("\n")
      : "N/A";

  const categoryLabels = {
    APLSK:
      "APLSK: Applied Design, Skills, and Technologies (e.g., Business, Technology, Home Economics, Media Arts, Tourism)",
    COMSERV:
      "COMSERV: Community Service (Volunteer Activity), which includes awareness of local, global, and cultural issues",
    FINE: "FINE: Fine Arts (e.g., Dance, Drama, Music, Visual Arts)",
    INDLC:
      "INDLC: Indigenous Languages and Culture, demonstrated at school or in the community",
    LNG: "LNG: Languages from the Languages Curriculum or External Assessments, including AP and IB courses",
    PHYSAC:
      "PHYSAC: Physical Activity (and Health) (e.g., Athletics, Dance, Gymnastics)",
    TRADES:
      "TRADES: Technical and Trades Training (e.g., Coding, Culinary Arts, Mechanics, Robotics, Woodwork)",
  };

  const selectedCategoryCode = normalizeDasCategoryCode(step2);

  const categoryLabel = selectedCategoryCode
    ? categoryLabels[selectedCategoryCode]
    : "No category chosen";

  return (
    <Document>
      {/* Page 1: Personal Information & Career Plans */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          <Image
            src="https://sas.nlsf.ca/nlsf-scholarships.png"
            style={styles.logo}
          />
        </View>

        {/* Personal Information */}
        <View style={styles.firstSection}>
          <Text style={styles.sectionTitle}>
            MINISTRY DOGWOOD-DISTRICT AUTHORITY SCHOLARSHIP
          </Text>
          <Text style={styles.sectionHeader}>PERSONAL INFORMATION</Text>
          <View style={styles.sectionRow}>
            {/** first row of the Personal Information */}
            <View style={styles.sectionHalfRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Legal Name: </Text>
                <Text style={styles.contentValue}>
                  {clipPdfText(step1.legalName)}
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>School: </Text>
                <Text style={styles.contentValue}>{schoolName}</Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Email: </Text>
                <Text style={styles.contentValue}>
                  {clipPdfText(step1.email || "N/A")}
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Date of Birth: </Text>
                <Text style={styles.contentValue}>
                  {formatYMDToMDY(step1?.dob)}
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Address: </Text>
                <Text style={styles.contentValue}>
                  {sanitizePdfText(
                    `${step1.address}, ${step1.city}, ${step1.province}, ${step1.postalCode}, ${step1.country}`
                  )}
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Citizenship: </Text>
                <Text style={styles.contentValue}>
                  {clipPdfText(step1.citizenship)}
                </Text>
              </View>
            </View>
            {/** second row of the Personal Information */}
            <View style={styles.sectionHalfRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>SIN #: </Text>
                <Text style={styles.contentValue}>{clipPdfText(step1.SIN)}</Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>Phone #: </Text>
                <Text style={styles.contentValue}>
                  {clipPdfText(step1.cellPhone)}
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabel}>PEN #: </Text>
                <Text style={styles.contentValue}>{clipPdfText(step1.PEN)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        {/* Career and Post-Secondary Plans */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            CAREER and POST-SECONDARY PLANS
          </Text>
          <View style={styles.sectionRowWithMargin}>
            <View style={styles.sectionContentRow}>
              <Text style={styles.contentLabelLongRow}>
                Are you planning on attending Post Secondary next year?{" "}
              </Text>
              <Text style={styles.contentValueLongRow}>
                {step2.direct_to_post_secondary ? "Yes" : "No"}
              </Text>
            </View>
          </View>
          <View style={styles.sectionRowWithMargin}>
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelLongRow}>
                  For which post-secondary program(s) do you plan to apply for?
                </Text>
              </View>
              <View style={styles.stackedTextRow}>
                <Text style={styles.stackedValueText}>
                  {programsApplyingForText}
                </Text>
              </View>
            </View>
          </View>
          {/* Row with vertical margin */}
          <View style={styles.rowMargin} />

          <View style={styles.sectionRowWithMargin}>
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelLongRow}>
                  What are your long term career plans?
                </Text>
              </View>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentValueLongRow}>
                  {clipPdfText(
                    step2.long_term_career_plans || step2.longTermCareerPlans,
                    MAX_DAS_PARAGRAPH_LENGTH
                  )}
                </Text>
              </View>
            </View>
          </View>
          {/* Horizontal Line */}
          <View style={styles.horizontalLine} />
        </View>

        {/* Courses */}
        {courses?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>COURSES</Text>
            <View style={styles.sectionRow}>
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentValueLongRow}>
                    List courses that you have taken related to your area of
                    interest:
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.sectionRow}>
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabelLongRow}>
                    Grade 11 and 12 Courses:
                  </Text>
                </View>
                {courses?.map((course, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(course.Data1)}
                    </Text>
                  </View>
                ))}
              </View>
              <View style={styles.sectionHalfRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabel}>Teacher/Sponsor</Text>
                </View>
                {courses?.map((course, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(course.Data2 || "N/A")}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}
        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        <View style={styles.footer}>
          <Text style={styles.headerText}>
            Page 1 of 3 - Generated on {generatedDate} for {step1.legalName}
          </Text>
        </View>
      </Page>

      {/* Page 2: Courses, Extracurriculars, Awards, and References */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          <Image
            src="https://sas.nlsf.ca/nlsf-scholarships.png"
            style={styles.logo}
          />
        </View>
        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        {/* Extracurricular Activities */}
        {extracurriculars?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>EXTRACURRICULAR ACTIVITIES</Text>
            <View style={styles.sectionRow}>
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentValueLongRow}>
                    List activities that are related to your area of interest:
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.sectionRow}>
              {/* Left Column: Extracurricular Activity Names */}
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabelLongRow}>
                    Extracurricular Activity:
                  </Text>
                </View>
                {extracurriculars?.map((activity, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(activity.Data1)}
                    </Text>
                  </View>
                ))}
              </View>

              {/* Right Column: Teacher/Sponsor */}
              <View style={styles.sectionHalfRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabel}>Teacher/Sponsor</Text>
                </View>
                {extracurriculars?.map((activity, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(activity.Data2 || "N/A")}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        {/* Awards */}
        {awards?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>AWARDS</Text>
            <View style={styles.sectionRow}>
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentValueLongRow}>
                    In this section list any awards, certificates or
                    competitions connected to your area of interest over the
                    last two years.{" "}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.sectionRow}>
              {/* Left Column: Award Name */}
              <View style={styles.sectionRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabelLongRow}>Award:</Text>
                </View>
                {awards?.map((award, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(award.Data1)}
                    </Text>
                  </View>
                ))}
              </View>

              {/* Right Column: Organization */}
              <View style={styles.sectionHalfRowItem}>
                <View style={styles.sectionContentRow}>
                  <Text style={styles.contentLabel}>Organization</Text>
                </View>
                {awards?.map((award, index) => (
                  <View key={index} style={styles.stackedTextRow}>
                    <Text style={styles.stackedValueText}>
                      {clipPdfText(award.Data2 || "N/A")}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        {/* References */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            TEACHER and/or COMMUNITY REFERENCE(S) FOR YOUR AREA OF INTEREST
          </Text>
          <View style={styles.sectionRow}>
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentValueLongRow}>
                  Please list a minimum of two teachers and/or community members
                  who can attest to your achievements in your chosen area of
                  interest and will be providing a written reference on your
                  behalf.
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.sectionRow}>
            {/* First Column for References */}
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelQuarterRow}>Name</Text>
              </View>
              {references.map((ref, index) => (
                <View key={index} style={styles.stackedTextRow}>
                  <Text style={styles.stackedValueText}>
                    {clipPdfText(ref.Name || "N/A")}
                  </Text>
                </View>
              ))}
            </View>
            {/* Second Column for References */}
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelQuarterRow}>Position</Text>
              </View>
              {references.map((ref, index) => (
                <View key={index} style={styles.stackedTextRow}>
                  <Text style={styles.stackedValueText}>
                    {clipPdfText(ref.Position || "N/A")}
                  </Text>
                </View>
              ))}
            </View>
            {/* Third Column for References */}
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelQuarterRow}>Phone Number</Text>
              </View>
              {references.map((ref, index) => (
                <View key={index} style={styles.stackedTextRow}>
                  <Text style={styles.stackedValueText}>
                    {clipPdfText(ref.Phone || "N/A")}
                  </Text>
                </View>
              ))}
            </View>
            {/* Fourth Column for References */}
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelQuarterRow}>Email</Text>
              </View>
              {references.map((ref, index) => (
                <View key={index} style={styles.stackedTextRow}>
                  <Text style={styles.stackedValueText}>
                    {clipPdfText(ref.Email || "N/A")}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Horizontal Line */}
        <View style={styles.horizontalLine} />

        {/* AREA OF EXCELLENCE */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>AREA OF EXCELLENCE: </Text>
          <View style={styles.sectionRow}>
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentValueLongRow}>{categoryLabel}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.headerText}>
            Page 2 of 3 - Generated on {generatedDate} for {step1.legalName}
          </Text>
        </View>
      </Page>

      {/* Page 3: Evidence of Achievement & Review */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {/* Logo */}
          <Image
            src="https://sas.nlsf.ca/nlsf-scholarships.png"
            style={styles.logo}
          />
        </View>

        {/* Evidence of Achievement */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>EVIDENCE OF ACHIEVEMENT</Text>
          {/* Row with vertical margin */}
          <View style={styles.rowMargin} />
          <View style={styles.sectionRow}>
            <View style={styles.sectionRowItem}>
              <View style={styles.sectionContentRow}>
                <Text style={styles.contentLabelLongRow}>
                  {clipPdfText(
                    step3.achievements || "No achievements provided.",
                    MAX_DAS_PARAGRAPH_LENGTH
                  )}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Review & Submit */}
        <View style={styles.section}>
          <Text style={styles.contentText}>
            Thank you for considering my application.
          </Text>
          <Text style={styles.contentText}>{clipPdfText(step1.legalName)}</Text>
        </View>
        <View style={styles.footer}>
          <Text style={styles.headerText}>
            Page 3 of 3 - Generated on {generatedDate} for {step1.legalName}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default memo(ApplicationPDF);
